import React from 'react';
import styled, { css } from 'styled-components';
import { Body5, Body7, VisuallyHidden } from '@ui';
import { GhostButton } from '@ui/buttons';
import { specDisplayName, specLabelById, specRequiresStamp } from './format';
import { matchSpecs } from './specSearch';

// The spec picker has two faces over the same list: a native <select> that shows every spec, and a
// typeahead that narrows the list as the analyst types. The list is the whole catalogue — the
// backfill specs route returns every spec at once, with no search parameter — so the typeahead
// filters in memory and never asks the server anything. Both faces hand the same `score_spec_id`
// up through `onChange`; only how the analyst lands on it differs.

export const SPEC_PICKER_MODES = {
  LIST: 'list',
  SEARCH: 'search',
};

// Which face is up is a preference, not a form field: it is not sent, and it is not seeded by
// "Start a similar run". It still has to outlive a trip to History and back, which remounts the
// form, so it lives in localStorage under this key. Storage can be absent or throw (private
// windows, blocked site data), and the picker renders fine without it, so every touch is guarded.
const MODE_STORAGE_KEY = 'scoreBackfill.specPickerMode';

const readStoredMode = () => {
  try {
    const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
    return stored === SPEC_PICKER_MODES.SEARCH ? SPEC_PICKER_MODES.SEARCH : SPEC_PICKER_MODES.LIST;
  } catch (e) {
    return SPEC_PICKER_MODES.LIST;
  }
};

const storeMode = (mode) => {
  try {
    window.localStorage.setItem(MODE_STORAGE_KEY, mode);
  } catch (e) {
    // A preference that cannot be stored is still a preference for this render.
  }
};

// How tall the floating results may be before something cuts them off.
//
// Two edges can: the bottom of the viewport, and the bottom of the nearest ancestor that clips
// its overflow — here the Page, whose overflow-x: auto makes its overflow-y auto as well, so in a
// real browser its computed overflow-y already reads 'auto' (the overflow-x check is for anything
// that reports the declared value instead). The walk stops at <body>: overflow set there belongs
// to the viewport, not to a box the list could be clipped by.
//
// The list starts RESULTS_GUTTER below the box, so that is taken off, and the result is floored at
// MIN_RESULTS_HEIGHT so a box at the very bottom of a short page still gets a usable list rather
// than a sliver — a little clipping beats three visible pixels.
export const MIN_RESULTS_HEIGHT = 160;
export const DEFAULT_RESULTS_HEIGHT = 320;
const RESULTS_GUTTER = 12;

const clipsOverflow = (value) => value === 'auto' || value === 'scroll' || value === 'hidden' || value === 'clip';
// overflow-x: clip does not force overflow-y the way auto/scroll/hidden do.
const forcesOverflowY = (value) => value === 'auto' || value === 'scroll' || value === 'hidden';

export const resultsMaxHeight = (anchor, { viewportHeight = window.innerHeight } = {}) => {
  if (!anchor || typeof anchor.getBoundingClientRect !== 'function') return DEFAULT_RESULTS_HEIGHT;

  let limit = viewportHeight;
  let node = anchor.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const { overflowY, overflowX } = window.getComputedStyle(node);
    if (clipsOverflow(overflowY) || forcesOverflowY(overflowX)) {
      limit = Math.min(limit, node.getBoundingClientRect().bottom);
      break;
    }
    node = node.parentElement;
  }

  const available = Math.floor(limit - anchor.getBoundingClientRect().bottom - RESULTS_GUTTER);
  return Math.max(MIN_RESULTS_HEIGHT, available);
};

const controlStyles = css`
  width: 100%;
  height: 40px;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 4px;
  padding: 0 8px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  color: ${({ theme }) => theme.themeColors.text};
  font: inherit;
  font-size: 14px;
`;

const PickerRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
`;

const ControlSlot = styled.div`
  flex: 1;
  min-width: 0;
`;

const SelectWrapper = styled.div`
  width: 100%;

  select {
    ${controlStyles}
  }
`;

const SearchWrapper = styled.div`
  position: relative;
  width: 100%;

  input {
    ${controlStyles}
    /* Leave room for the clear control so a long name does not run under it. */
    padding-right: 36px;

    &::placeholder {
      color: ${({ theme }) => theme.themeColors.secondaryText};
    }

    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
      outline-offset: 1px;
    }
  }
`;

const ClearButton = styled(GhostButton)`
  position: absolute;
  top: 0;
  right: 0;
  height: 40px;
  width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.themeColors.secondaryText};
  font-size: 16px;
  line-height: 1;

  &:focus-visible {
    outline-offset: -2px;
  }
`;

// The results float over whatever is below, like the stock search bar's. The Page they live in
// clips anything positioned past its bottom edge (its overflow-x: auto makes overflow-y auto too),
// so the Score Backfill page sets a min-height that leaves room for this list under the picker,
// and the list's max-height is measured against that edge (resultsMaxHeight) rather than fixed.
const ResultsWrapper = styled.div`
  position: absolute;
  top: 44px;
  left: 0;
  right: 0;
  z-index: 1000;
  overflow: auto;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 4px;
  box-shadow: 0px 2px 4px 1px rgba(0, 0, 0, 0.1);
`;

const ResultRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  cursor: pointer;
  background: ${({ theme, $isActive }) => ($isActive ? theme.themeColors.primaryCtaButton : 'transparent')};

  &:last-child {
    border-bottom: none;
  }

  /* Only the highlighted row recolours its text; an idle row keeps the description low-contrast. */
  ${({ theme, $isActive }) =>
    $isActive &&
    css`
      * {
        color: ${theme.themeColors.buttonText};
      }
    `}
`;

const EmptyRow = styled.div`
  padding: 12px;
`;

const Description = styled(Body7)`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SegmentedWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  height: 40px;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 4px;
  overflow: hidden;
`;

const Segment = styled(GhostButton)`
  padding: 0 12px;
  white-space: nowrap;
  background: transparent;

  & + & {
    border-left: 1px solid ${({ theme }) => theme.themeColors.border};
  }

  &[aria-pressed='true'] {
    background: ${({ theme }) => theme.themeColors.primaryCtaButton};

    * {
      color: ${({ theme }) => theme.themeColors.buttonText};
    }
  }

  &:focus-visible {
    outline-offset: -2px;
  }
`;

const MODE_OPTIONS = [
  { value: SPEC_PICKER_MODES.LIST, label: 'Browse' },
  { value: SPEC_PICKER_MODES.SEARCH, label: 'Search' },
];

const ModeToggle = ({ mode, onChange }) => (
  <SegmentedWrapper role="group" aria-label="Score spec picker">
    {MODE_OPTIONS.map((option) => (
      <Segment key={option.value} aria-pressed={mode === option.value} onClick={() => onChange(option.value)}>
        <Body5>{option.label}</Body5>
      </Segment>
    ))}
  </SegmentedWrapper>
);

// A spec with a present-day context setting (backfill_allowed: false on its entry) is backfilled
// stamped at scoring time: the form switches to a start-date-only, latest-per-company run for it. It
// stays selectable — that run is a real backfill — but both faces say so before it is chosen, so the
// different form is not a surprise. A native <option> holds text only, hence a suffix; the same words
// the form's notice opens with.
const STAMP_MARKER = 'present-day context';

const optionLabel = (spec) =>
  specRequiresStamp(spec) ? `${specDisplayName(spec)} — ${STAMP_MARKER}` : specDisplayName(spec);

const SpecList = ({ specs, value, onChange }) => (
  <SelectWrapper>
    <select
      id="score-spec-select"
      aria-label="Score spec"
      onChange={(e) => onChange(e.target.value)}
      value={value || ''}
    >
      <option value={''}>Select a score spec…</option>
      {specs.map((s) => (
        <option key={s.score_spec_id} value={s.score_spec_id}>
          {optionLabel(s)}
        </option>
      ))}
    </select>
  </SelectWrapper>
);

const LISTBOX_ID = 'score-spec-search-results';
const optionId = (index) => `score-spec-search-option-${index}`;

// The typeahead. `query` is null while the analyst is not typing, and the input then shows the
// chosen spec's name (or nothing). Typing sets it to the text, which opens the results; picking a
// result, Escape, or leaving the field sets it back to null, so an abandoned search never leaves a
// half-typed string over a real selection. The chosen spec changes only through `onChange`.
const SpecSearch = ({ specs, value, onChange, autoFocus }) => {
  const inputRef = React.useRef(null);
  const [query, setQuery] = React.useState(null);
  const [activeIndex, setActiveIndex] = React.useState(-1);

  const isEditing = query !== null;
  const results = React.useMemo(() => (isEditing ? matchSpecs(specs, query) : []), [specs, query, isEditing]);
  const isOpen = isEditing && query.trim() !== '';
  const inputValue = isEditing ? query : value ? specLabelById(specs, value) : '';

  // Measured each time the list opens, and again on resize and on any scroll (capture, so a
  // scrolling ancestor counts too): both move the box relative to the edges that bound the list.
  // useLayoutEffect so the first paint of an open list already has its height.
  const [maxHeight, setMaxHeight] = React.useState(DEFAULT_RESULTS_HEIGHT);
  React.useLayoutEffect(() => {
    if (!isOpen) return undefined;
    const measure = () => setMaxHeight(resultsMaxHeight(inputRef.current));
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [isOpen]);

  const select = (spec) => {
    onChange(spec.score_spec_id);
    setQuery(null);
    setActiveIndex(-1);
  };

  const closeAndRestore = () => {
    setQuery(null);
    setActiveIndex(-1);
  };

  const handleChange = (e) => {
    setQuery(e.target.value);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      if (!isOpen || results.length === 0) return;
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      if (!isOpen || results.length === 0) return;
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (!isOpen || results.length === 0) return;
      e.preventDefault();
      // Enter with nothing highlighted takes the top match, so "type, Enter" is the whole gesture.
      select(results[activeIndex >= 0 ? activeIndex : 0]);
    } else if (e.key === 'Escape') {
      if (!isEditing) return;
      e.preventDefault();
      closeAndRestore();
    }
  };

  const handleClear = () => {
    onChange('');
    closeAndRestore();
    inputRef.current?.focus();
  };

  const canClear = !!value && !isEditing;

  return (
    <SearchWrapper>
      <input
        ref={inputRef}
        id="score-spec-search"
        type="text"
        role="combobox"
        aria-label="Search score specs"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls={isOpen ? LISTBOX_ID : undefined}
        aria-activedescendant={isOpen && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        autoComplete="off"
        autoFocus={autoFocus}
        placeholder="Type to search score specs…"
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={closeAndRestore}
        // The input shows the chosen name while idle. Selecting it on focus means the next keystroke
        // replaces it rather than appending to it.
        onFocus={(e) => e.target.select()}
      />
      {canClear && (
        <ClearButton aria-label="Clear score spec" onClick={handleClear}>
          <span aria-hidden="true">×</span>
        </ClearButton>
      )}
      {isOpen && (
        // Pressing on the list would blur the input and close it before the click lands; swallowing
        // the mousedown keeps focus where it is so the click can select.
        <ResultsWrapper
          data-testid="score-spec-search-results"
          style={{ maxHeight }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {results.length === 0 ? (
            <EmptyRow>
              <Body5 isLowContrast>No score specs match “{query.trim()}”</Body5>
            </EmptyRow>
          ) : (
            <div id={LISTBOX_ID} role="listbox" aria-label="Matching score specs">
              {results.map((spec, index) => (
                <ResultRow
                  key={spec.score_spec_id}
                  id={optionId(index)}
                  role="option"
                  aria-selected={index === activeIndex}
                  $isActive={index === activeIndex}
                  onClick={() => select(spec)}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <Body5>{specDisplayName(spec)}</Body5>
                  {spec.description && <Description isLowContrast>{spec.description}</Description>}
                  {specRequiresStamp(spec) && (
                    <Description isLowContrast>Present-day context — stamped at scoring time</Description>
                  )}
                </ResultRow>
              ))}
            </div>
          )}
        </ResultsWrapper>
      )}
      <VisuallyHidden aria-live="polite">
        {isOpen && `${results.length} matching score spec${results.length === 1 ? '' : 's'}`}
      </VisuallyHidden>
    </SearchWrapper>
  );
};

export const SpecSelect = ({ specs, value, onChange }) => {
  const [mode, setMode] = React.useState(readStoredMode);
  // Only a switch the analyst made moves focus into the search box. On a plain mount the stored
  // preference decides the face and focus stays where the page put it.
  const [modeTouched, setModeTouched] = React.useState(false);

  const handleModeChange = (next) => {
    if (next === mode) return;
    setMode(next);
    setModeTouched(true);
    storeMode(next);
  };

  if (!specs) {
    return (
      <SelectWrapper>
        <select onChange={() => null} value={'loading'} disabled aria-busy="true" aria-label="Loading score specs">
          <option value={'loading'}>Loading…</option>
        </select>
      </SelectWrapper>
    );
  }

  return (
    <PickerRow>
      <ControlSlot>
        {mode === SPEC_PICKER_MODES.SEARCH ? (
          <SpecSearch specs={specs} value={value} onChange={onChange} autoFocus={modeTouched} />
        ) : (
          <SpecList specs={specs} value={value} onChange={onChange} />
        )}
      </ControlSlot>
      <ModeToggle mode={mode} onChange={handleModeChange} />
    </PickerRow>
  );
};
